import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';
import {
  ArrowLeft,
  Plus,
  Search,
  Upload,
  Download,
  Pencil,
  Trash2,
  X,
  Tag,
  Loader2,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

interface GlossaryTerm {
  id: string;
  sourceTerm: string;
  sourceLanguage: string;
  translations: Record<string, string>;
  doNotTranslate: boolean;
  caseSensitive: boolean;
  notes?: string;
  examples?: string[];
}

interface Glossary {
  id: string;
  name: string;
  description?: string;
  domain?: string;
  terms: GlossaryTerm[];
}

const GlossaryEditor: React.FC = () => {
  const { glossaryId } = useParams<{ glossaryId: string }>();
  const navigate = useNavigate();

  const [glossary, setGlossary] = useState<Glossary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddTermModal, setShowAddTermModal] = useState(false);
  const [editingTerm, setEditingTerm] = useState<GlossaryTerm | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [languages, setLanguages] = useState<any[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvContent, setCsvContent] = useState('');

  const [newTerm, setNewTerm] = useState({
    sourceTerm: '',
    sourceLanguage: 'en',
    translations: {} as Record<string, string>,
    doNotTranslate: false,
    caseSensitive: false,
    notes: '',
    examples: [] as string[]
  });

  useEffect(() => {
    loadLanguages();
    loadGlossary();
  }, [glossaryId]);

  const loadLanguages = async () => {
    try {
      const langs = await apiService.getLanguages();
      setLanguages(langs);
    } catch (err) {
      console.error('Error loading languages:', err);
    }
  };

  const loadGlossary = async () => {
    if (!glossaryId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getGlossaryById(glossaryId);
      setGlossary(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load glossary');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTerm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!glossaryId) return;

    try {
      await apiService.addGlossaryTerm(glossaryId, newTerm);
      setShowAddTermModal(false);
      setNewTerm({
        sourceTerm: '',
        sourceLanguage: 'en',
        translations: {},
        doNotTranslate: false,
        caseSensitive: false,
        notes: '',
        examples: []
      });
      loadGlossary();
    } catch (err: any) {
      alert(err.message || 'Failed to add term');
    }
  };

  const handleUpdateTerm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTerm) return;

    try {
      await apiService.updateGlossaryTerm(editingTerm.id, {
        translations: editingTerm.translations,
        doNotTranslate: editingTerm.doNotTranslate,
        caseSensitive: editingTerm.caseSensitive,
        notes: editingTerm.notes,
        examples: editingTerm.examples
      });
      setEditingTerm(null);
      loadGlossary();
    } catch (err: any) {
      alert(err.message || 'Failed to update term');
    }
  };

  const handleDeleteTerm = async (termId: string) => {
    if (!window.confirm('Are you sure you want to delete this term?')) {
      return;
    }

    try {
      await apiService.deleteGlossaryTerm(termId);
      loadGlossary();
    } catch (err: any) {
      alert(err.message || 'Failed to delete term');
    }
  };

  const handleExportCSV = async () => {
    if (!glossaryId) return;

    try {
      const csv = await apiService.exportGlossaryToCSV(glossaryId);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${glossary?.name || 'glossary'}_export.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Failed to export CSV');
    }
  };

  const handleImportCSV = async () => {
    if (!glossaryId || !csvContent) return;

    try {
      const result = await apiService.importGlossaryFromCSV(glossaryId, csvContent);
      alert(`Imported ${result.imported} terms successfully. Updated: ${result.updated}, Failed: ${result.failed}`);
      setShowImportModal(false);
      setCsvContent('');
      loadGlossary();
    } catch (err: any) {
      alert(err.message || 'Failed to import CSV');
    }
  };

  const filteredTerms = glossary?.terms.filter(term =>
    term.sourceTerm.toLowerCase().includes(searchQuery.toLowerCase()) ||
    term.notes?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-5 w-48" />
          <div className="flex justify-between items-center">
            <Skeleton className="h-10 w-64" />
            <div className="flex space-x-2">
              <Skeleton className="h-10 w-28" />
              <Skeleton className="h-10 w-28" />
              <Skeleton className="h-10 w-28" />
            </div>
          </div>
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="py-4">
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !glossary) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="border-destructive/50">
          <CardContent className="py-6">
            <div className="flex items-center text-destructive">
              <AlertCircle className="h-5 w-5 mr-2" />
              <p>{error || 'Glossary not found'}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/glossaries')}
              className="mt-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Glossaries
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/glossaries')}
          className="mb-4 -ml-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Glossaries
        </Button>

        <h1 className="text-3xl font-bold text-foreground">{glossary.name}</h1>
        {glossary.description && (
          <p className="mt-2 text-muted-foreground">{glossary.description}</p>
        )}
        <div className="mt-2 flex items-center space-x-4 text-sm text-muted-foreground">
          <span>{glossary.terms.length} terms</span>
          {glossary.domain && (
            <>
              <span>•</span>
              <Badge variant="secondary" className="capitalize">{glossary.domain}</Badge>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex-1 max-w-lg relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search terms..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={() => setShowImportModal(true)}
          >
            <Upload className="h-4 w-4 mr-2" />
            Import CSV
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCSV}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => setShowAddTermModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Term
          </Button>
        </div>
      </div>

      {/* Terms List */}
      {filteredTerms.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Tag className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h3 className="mt-4 text-lg font-medium text-foreground">No terms found</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {searchQuery ? 'Try a different search query' : 'Get started by adding a new term.'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setShowAddTermModal(true)} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add First Term
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTerms.map((term) => (
            <Card key={term.id} className="hover:shadow-md transition-shadow">
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                      <h4 className="text-lg font-medium text-foreground">{term.sourceTerm}</h4>
                      {term.doNotTranslate && (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                          Do Not Translate
                        </Badge>
                      )}
                      {term.caseSensitive && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-800">
                          Case Sensitive
                        </Badge>
                      )}
                    </div>

                    {term.notes && (
                      <p className="mt-1 text-sm text-muted-foreground">{term.notes}</p>
                    )}

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      {Object.entries(term.translations).map(([langCode, translation]) => (
                        <div key={langCode} className="flex items-center text-sm bg-muted/50 rounded px-2 py-1">
                          <span className="font-medium text-muted-foreground w-10">{langCode.toUpperCase()}:</span>
                          <span className="text-foreground">{translation}</span>
                        </div>
                      ))}
                    </div>

                    {term.examples && term.examples.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Examples:</p>
                        <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                          {term.examples.map((example, idx) => (
                            <li key={idx}>{example}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-1 ml-4">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingTerm(term)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteTerm(term.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Term Modal */}
      {showAddTermModal && (
        <TermModal
          title="Add New Term"
          term={newTerm}
          languages={languages}
          onSave={handleAddTerm}
          onCancel={() => {
            setShowAddTermModal(false);
            setNewTerm({
              sourceTerm: '',
              sourceLanguage: 'en',
              translations: {},
              doNotTranslate: false,
              caseSensitive: false,
              notes: '',
              examples: []
            });
          }}
          onChange={setNewTerm}
        />
      )}

      {/* Edit Term Modal */}
      {editingTerm && (
        <TermModal
          title="Edit Term"
          term={editingTerm}
          languages={languages}
          onSave={handleUpdateTerm}
          onCancel={() => setEditingTerm(null)}
          onChange={setEditingTerm}
          isEditing
        />
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start justify-center pt-20 overflow-y-auto">
          <Card className="w-full max-w-2xl mx-4 relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2"
              onClick={() => setShowImportModal(false)}
            >
              <X className="h-4 w-4" />
            </Button>

            <CardHeader>
              <CardTitle>Import Terms from CSV</CardTitle>
              <CardDescription>
                Upload a CSV file with your glossary terms
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm font-medium text-foreground mb-2">CSV Format:</p>
                <code className="text-xs text-muted-foreground block bg-background p-2 rounded">
                  source_term,source_language,target_language,target_term,do_not_translate,case_sensitive,notes
                </code>
              </div>

              <div className="space-y-2">
                <Label>CSV Content</Label>
                <Textarea
                  value={csvContent}
                  onChange={(e) => setCsvContent(e.target.value)}
                  rows={10}
                  className="font-mono text-sm"
                  placeholder="Paste CSV content here..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowImportModal(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleImportCSV}>
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Term Modal Component
const TermModal: React.FC<{
  title: string;
  term: any;
  languages: any[];
  onSave: (e: React.FormEvent) => void;
  onCancel: () => void;
  onChange: (term: any) => void;
  isEditing?: boolean;
}> = ({ title, term, languages, onSave, onCancel, onChange, isEditing }) => {
  const [exampleInput, setExampleInput] = useState('');

  const addExample = () => {
    if (exampleInput.trim()) {
      onChange({
        ...term,
        examples: [...(term.examples || []), exampleInput.trim()]
      });
      setExampleInput('');
    }
  };

  const removeExample = (index: number) => {
    onChange({
      ...term,
      examples: term.examples.filter((_: any, i: number) => i !== index)
    });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-start justify-center pt-10 overflow-y-auto">
      <Card className="w-full max-w-3xl mx-4 my-10 relative">
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2"
          onClick={onCancel}
        >
          <X className="h-4 w-4" />
        </Button>

        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>

        <form onSubmit={onSave}>
          <CardContent className="space-y-6 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Source Term *</Label>
                <Input
                  required
                  disabled={isEditing}
                  value={term.sourceTerm}
                  onChange={(e) => onChange({ ...term, sourceTerm: e.target.value })}
                  placeholder="iPhone"
                />
              </div>

              <div className="space-y-2">
                <Label>Source Language *</Label>
                <select
                  required
                  disabled={isEditing}
                  value={term.sourceLanguage}
                  onChange={(e) => onChange({ ...term, sourceLanguage: e.target.value })}
                  className={cn(
                    "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                    "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    "disabled:cursor-not-allowed disabled:opacity-50"
                  )}
                >
                  {languages.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-3">
              <Label>Translations</Label>
              <div className="space-y-2">
                {languages.filter(l => l.code !== term.sourceLanguage).slice(0, 6).map(lang => (
                  <div key={lang.code} className="flex items-center space-x-2">
                    <span className="w-24 text-sm font-medium text-muted-foreground">{lang.name}:</span>
                    <Input
                      value={term.translations[lang.code] || ''}
                      onChange={(e) => onChange({
                        ...term,
                        translations: { ...term.translations, [lang.code]: e.target.value }
                      })}
                      placeholder={`Translation in ${lang.name}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={term.notes || ''}
                onChange={(e) => onChange({ ...term, notes: e.target.value })}
                rows={2}
                placeholder="Context, usage notes, etc."
              />
            </div>

            <div className="flex space-x-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={term.doNotTranslate}
                  onChange={(e) => onChange({ ...term, doNotTranslate: e.target.checked })}
                  className="h-4 w-4 text-primary border-input rounded focus:ring-primary"
                />
                <span className="ml-2 text-sm text-foreground">Do Not Translate</span>
              </label>

              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={term.caseSensitive}
                  onChange={(e) => onChange({ ...term, caseSensitive: e.target.checked })}
                  className="h-4 w-4 text-primary border-input rounded focus:ring-primary"
                />
                <span className="ml-2 text-sm text-foreground">Case Sensitive</span>
              </label>
            </div>

            <div className="space-y-2">
              <Label>Examples</Label>
              <div className="flex space-x-2">
                <Input
                  value={exampleInput}
                  onChange={(e) => setExampleInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addExample())}
                  placeholder="Add an example sentence..."
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addExample}
                >
                  Add
                </Button>
              </div>
              {term.examples && term.examples.length > 0 && (
                <ul className="space-y-1 mt-2">
                  {term.examples.map((example: string, idx: number) => (
                    <li key={idx} className="flex items-center justify-between text-sm bg-muted/50 px-3 py-2 rounded">
                      <span className="text-foreground">{example}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeExample(idx)}
                      >
                        <X className="h-3 w-3 text-destructive" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>

          <div className="border-t px-6 py-4 flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">
              <CheckCircle className="h-4 w-4 mr-2" />
              {isEditing ? 'Update Term' : 'Add Term'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default GlossaryEditor;
